import React, { useState, useEffect, useRef } from 'react';
import { 
  PlusIcon,
  CameraIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BeakerIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ChartPieIcon,
  DocumentTextIcon,
  XMarkIcon,
  XCircleIcon,
  LockClosedIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import havvaService from '../services/havvaService';
import havvaAnalysisService from '../services/havvaAnalysisService';
import { lookupRegisteredFarmerId, resolveFarmerId } from '../services/farmIdentity';
import { useFarm } from '../contexts/FarmContext';
import { useAuth } from '../contexts/AuthContext';
import { IOT_VENDORS } from '../config/iotVendors';
import { supabase } from '../config/supabase';
import FarmSelector from '../components/FarmSelector';
import OrganizationSelector from '../components/OrganizationSelector';
import SearchableFarmSelect from '../components/SearchableFarmSelect';
import PageErrorBoundary from '../components/PageErrorBoundary';
import { HavvaAnalysisDetails, HavvaNewFormatResult } from '../components/HavvaAnalysisResult';
import {
  normalizeHavvaAnalysis,
  normalizeHavvaDetail,
  normalizeHavvaHistory,
  formatPercentage,
  clampPercentage,
  formatHavvaDate,
  parsePercentage,
  toDisplayText,
  isHttpUrl,
  isImageDataUri,
  isSignedUrl
} from '../utils/havvaAnalysis';

const UPLOAD_HISTORY_KEY = 'havva_upload_history';
const CAMERA_LOGS_KEY = 'havva_camera_logs';

// Limits for the steps that follow a successful analysis, so the upload form
// can never sit in "Saving..." for ever.
const IMAGE_FETCH_TIMEOUT_MS = 15000;     // download of HAVVA's annotated image
const STORAGE_UPLOAD_TIMEOUT_MS = 120000; // copy of the photo to Supabase storage
const HISTORY_SAVE_TIMEOUT_MS = 30000;    // insert into havva_analysis_history

// Resolve with `fallback` when the promise has not settled after `ms`
const withTimeout = (promise, ms, fallback) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => resolve(fallback), ms);
  Promise.resolve(promise).then(
    (value) => { clearTimeout(timer); resolve(value); },
    (error) => { clearTimeout(timer); reject(error); }
  );
});

// The middleware is fragile: camera-list requests go out two at a time
const CAMERA_LIST_CONCURRENCY = 2;

// One camera record. The middleware's record id is unique; the camera id is
// not (two farms can both have camera "2"), so lists and selects key on this.
const cameraKey = (camera) => String(camera.id ?? camera.cameraId);

// The farm an analysis is stored under: a whole number (farm_id), or null
// when the camera has no usable farm id.
const toStoredFarmId = (farmId) => {
  const parsed = parseInt(farmId, 10);
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
};

// Run `worker` over `items` with at most `limit` running at once.
// Resolves with the results in the order of `items`.
const runWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
};

// Image types that are kept as a copy in Supabase storage
const IMAGE_EXTENSIONS = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif' };

// The type of an image read from its first bytes, or null. The content type
// a server declares is not always right: a JPEG may arrive as
// "application/octet-stream" or "image/jpg".
const sniffImageType = async (blob) => {
  try {
    const bytes = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const startsWith = (...signature) => signature.every((byte, index) => bytes[index] === byte);
    if (startsWith(0xFF, 0xD8, 0xFF)) return 'image/jpeg';
    if (startsWith(0x89, 0x50, 0x4E, 0x47)) return 'image/png';
    if (startsWith(0x47, 0x49, 0x46, 0x38)) return 'image/gif';
    if (startsWith(0x52, 0x49, 0x46, 0x46) && [0x57, 0x45, 0x42, 0x50].every((byte, index) => bytes[8 + index] === byte)) return 'image/webp';
  } catch (e) {
    // Unreadable here: the declared type decides
  }
  return null;
};

// Download an image (HAVVA's annotated image URL, or a data: URI) so a copy
// can be stored. Resolves with { blob, type, ext }, or null when it cannot be
// read from the browser: blocked by CORS, 401 / 404, not an image, or no
// answer within IMAGE_FETCH_TIMEOUT_MS. `signal` stops it early (leaving the
// page). Never rejects.
// HAVVA's image hosts send no CORS header, so the browser cannot download the
// marked-up photo directly; those links are fetched through our own server
// function (api/havva/image.js). Any other link is fetched as before.
const HAVVA_IMAGE_HOSTS = ['kapitani-api.havva.my', 'sirim.havva.my'];
const imageFetchUrl = (url) => {
  try {
    const { protocol, hostname } = new URL(url);
    if (protocol === 'https:' && HAVVA_IMAGE_HOSTS.includes(hostname)) {
      return `/api/havva/image?url=${encodeURIComponent(url)}`;
    }
  } catch (e) {
    // not an absolute URL: fetch it as given
  }
  return url;
};

const fetchImageBlob = async (url, signal = null) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort);
  }
  try {
    const response = await fetch(imageFetchUrl(url), { credentials: 'omit', signal: controller.signal });
    if (!response.ok) {
      console.warn('[HAVVA] Annotated image not serving (HTTP', response.status + ')');
      return null;
    }
    const blob = await response.blob();
    if (blob.size === 0) return null;
    // What the bytes are counts; the declared type only when they are not recognised
    const declared = (blob.type || '').split(';')[0].trim().toLowerCase();
    const type = (await sniffImageType(blob)) || (IMAGE_EXTENSIONS[declared] ? declared : null);
    if (!type) return null;
    // Supabase storage files a Blob under the Blob's own type, so the copy
    // carries the real one (not "application/octet-stream")
    return { blob: declared === type ? blob : new Blob([blob], { type }), type, ext: IMAGE_EXTENSIONS[type] };
  } catch (e) {
    return null;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
};

// localStorage can throw (blocked storage, full quota); a failed write must
// never take the page down.
const writeStorage = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`[HAVVA] Could not write localStorage "${key}":`, e);
  }
};

// The API activity log from localStorage: { [cameraId]: [entry, ...] }.
// Anything else (null, an array, a string) is discarded and the key removed.
const loadStoredCameraLogs = () => {
  try {
    const saved = localStorage.getItem(CAMERA_LOGS_KEY);
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    const valid = parsed && typeof parsed === 'object' && !Array.isArray(parsed) &&
      Object.values(parsed).every(Array.isArray);
    if (!valid) {
      writeStorage(CAMERA_LOGS_KEY, null);
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).map(([cameraId, logs]) => [cameraId, logs.filter(log => log && typeof log === 'object')])
    );
  } catch (e) {
    console.error('Error loading logs:', e);
    return {};
  }
};

// Read the localStorage copy of the upload history through the normaliser.
// Entries without any analysis (the "N/A" uploads stored before the Oct-2026
// fix, which blanked the page) are dropped and the cleaned list is written
// back, so an affected browser heals itself on the next load.
const loadStoredUploadHistory = () => {
  let saved = null;
  try {
    saved = localStorage.getItem(UPLOAD_HISTORY_KEY);
  } catch (e) {
    return [];
  }
  if (!saved) return [];

  let cleaned = [];
  try {
    cleaned = normalizeHavvaHistory(JSON.parse(saved), { dropEmpty: true });
  } catch (e) {
    console.error('Error loading localStorage history:', e);
  }

  try {
    if (cleaned.length === 0) {
      localStorage.removeItem(UPLOAD_HISTORY_KEY);
    } else {
      const serialized = JSON.stringify(cleaned);
      if (serialized !== saved) localStorage.setItem(UPLOAD_HISTORY_KEY, serialized);
    }
  } catch (e) {
    console.warn('[HAVVA] Could not rewrite localStorage history:', e);
  }
  return cleaned;
};

// The history list of the page: the records read from the database plus the
// entries of this browser's backup copy that never reached it (saveToHistory
// writes the backup when the database save fails or times out). A backup
// entry with a dbId is a copy of a database row, and one whose analysis id
// was just read from the database was saved after all; neither is added, so
// nothing shows twice. Newest first.
const mergeBackupHistory = (saved, stored) => {
  const seen = new Set(saved.map(entry => String(entry.id)));
  const backupOnly = stored.filter((entry) => {
    const id = String(entry.id);
    if ((entry.dbId !== undefined && entry.dbId !== null) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  if (backupOnly.length === 0) return saved;
  return [...backupOnly, ...saved].sort(
    (a, b) => (Date.parse(b.timeStamp) || 0) - (Date.parse(a.timeStamp) || 0)
  );
};

const CameraDetailModal = ({ isOpen, onClose, camera }) => {
  if (!isOpen || !camera) return null;

  const getHealthColor = (value) => {
    if (value <= 5) return 'text-green-600 bg-green-50';
    if (value <= 15) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Healthy':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'Alert':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Camera Analysis Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            ✕
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Camera Info */}
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Camera Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Camera ID:</span>
                  <span className="font-medium">{camera.cameraId}</span>
                </div>
                {toDisplayText(camera.cameraStatus) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(camera.cameraStatus)}
                      <span className="font-medium">{toDisplayText(camera.cameraStatus)}</span>
                    </div>
                  </div>
                )}
                {toDisplayText(camera.cameraQuality) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Quality:</span>
                    <span className="font-medium">{toDisplayText(camera.cameraQuality)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Note:</span>
                  <span className="font-medium">{camera.note}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Image Display */}
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Latest Analysis</h4>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <CameraIcon className="h-12 w-12 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Recommended Activity - only when the middleware returns one */}
        {toDisplayText(camera.recommendedActivity) && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Recommended Activity</h4>
            <p className="text-blue-800">{toDisplayText(camera.recommendedActivity)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Edit Camera Modal - API 3
const EditCameraModal = ({ isOpen, onClose, camera, onSave, farms = [] }) => {
  const [formData, setFormData] = useState({
    cameraId: camera?.cameraId || '',
    note: camera?.note || '',
    farmerId: camera?.farmerId || '',
    farmId: camera?.farmId || '',
    vendorId: camera?.vendorId || ''
  });
  // Display only: the farmer id of the chosen farm's middleware record, when
  // it has one. handleSaveEditCamera sends the edit under it (see
  // farmIdentity), so the Farmer ID box shows it and is locked; without a
  // record the box keeps the camera's own id. Cleared whenever the farm
  // changes, so another farm's id is never shown while its lookup runs.
  const [registeredFarmerId, setRegisteredFarmerId] = useState(null);

  React.useEffect(() => {
    if (camera) {
      setFormData({
        cameraId: camera.cameraId || '',
        note: camera.note || '',
        farmerId: camera.farmerId || '',
        farmId: camera.farmId || '',
        vendorId: camera.vendorId || ''
      });
    }
  }, [camera]);

  React.useEffect(() => {
    let cancelled = false;
    setRegisteredFarmerId(null);
    lookupRegisteredFarmerId(formData.farmId).then((registered) => {
      if (!cancelled) setRegisteredFarmerId(registered);
    });
    return () => { cancelled = true; };
  }, [formData.farmId]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.cameraId || !formData.note) {
      alert('Please fill in all required fields');
      return;
    }

    const updatedCamera = {
      ...camera,
      ...formData
    };

    onSave(updatedCamera);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <PencilIcon className="h-6 w-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900">Edit Camera</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Camera ID *
              </label>
              <input 
                type="text" 
                value={formData.cameraId}
                onChange={(e) => setFormData({...formData, cameraId: e.target.value})}
                className="input bg-gray-50"
                readOnly
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Farm ID *
              </label>
              <SearchableFarmSelect
                farms={farms}
                value={formData.farmId}
                onChange={(val) => setFormData({...formData, farmId: val})}
                name="farmId"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Farmer ID *
              </label>
              <input
                type="text"
                value={registeredFarmerId || formData.farmerId}
                onChange={(e) => setFormData({...formData, farmerId: e.target.value})}
                className={`input ${registeredFarmerId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="e.g., F001"
                readOnly={Boolean(registeredFarmerId)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vendor ID *
              </label>
              <input 
                type="text" 
                value={formData.vendorId}
                onChange={(e) => setFormData({...formData, vendorId: e.target.value})}
                className="input"
                placeholder="e.g., V001"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note *
            </label>
            <textarea 
              value={formData.note}
              onChange={(e) => setFormData({...formData, note: e.target.value})}
              className="input"
              rows="3"
              placeholder="Describe the camera location and purpose..."
              required
            />
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-900 mb-2">API Response Format</h4>
            <div className="text-xs text-green-700">
              <div>• <strong>status:</strong> Success/Failure status of the edit operation</div>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary"
            >
              Update Camera
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const HAVVAContent = () => {
  // Get current user from AuthContext
  const { user } = useAuth();
  
  // Get HAVVA vendor config
  const havvaVendorId = IOT_VENDORS.HAVVA.vendorId;
  
  // Get real farms from FarmContext (Supabase)
  const { farms: contextFarms, loading: farmsLoading, selectedFarmId, selectedOrgId, isAdmin, hasOrganizations, hasFarms } = useFarm();
  
  // Transform farms to match expected format
  const farms = contextFarms?.map(f => ({
    id: f.farm_id,
    name: `${f.farm_name || 'Unnamed Farm'}${f.farmsize ? ` (${f.farmsize} hectares)` : ''}`,
    farmName: f.farm_name,
    status: 'Active'
  })) || [];

  const [cameras, setCameras] = useState([]);
  const [selectedTab, setSelectedTab] = useState('list');
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [isDetailModalOpen, setIsDetailModelOpen] = useState(false);
  const [isEditCameraModalOpen, setIsEditCameraModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false); // Detail Analysis tab only
  const [error, setError] = useState(null);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [detailAnalysis, setDetailAnalysis] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [cameraLogs, setCameraLogs] = useState({}); // { [cameraId]: [logs] }
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedCameraForLog, setSelectedCameraForLog] = useState(null);
  const [logPage, setLogPage] = useState(1);
  const [logsPerPage] = useState(5); // Show 5 logs per page
  const [addCameraFarmId, setAddCameraFarmId] = useState(''); // For add camera form
  const [addCameraFarmerId, setAddCameraFarmerId] = useState(''); // Farmer id the add camera form will send (see farmIdentity)
  const [selectedFile, setSelectedFile] = useState(null); // For upload preview
  const [filePreview, setFilePreview] = useState(null); // For image preview URL
  const [uploadHistory, setUploadHistory] = useState([]); // For analysis history from uploads
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null); // For viewing analysis detail
  const [detailStatus, setDetailStatus] = useState(null); // { type: 'unavailable' | 'error', message? } for Detail Analysis tab
  const [storedDetail, setStoredDetail] = useState(null); // Detail Analysis tab: latest stored analysis of the camera (upload history)
  const [detailCamera, setDetailCamera] = useState(null); // Detail Analysis tab: the camera chosen in its select (selectedCamera belongs to the list's modals)
  const [uploadPhase, setUploadPhase] = useState(null); // null | 'analyzing' (middleware call, cancellable) | 'saving'
  const [uploadResult, setUploadResult] = useState(null); // { type: 'success' | 'no-analysis' | 'cancelled' | 'error', id?, message? }
  const uploadAbortRef = useRef(null); // AbortController of the running upload
  const detailRequestRef = useRef(0); // Ignore out-of-order detail-analysis responses
  const cameraLoadRef = useRef(0); // Only the newest camera-list load may ask the middleware / set state
  const cameraLoadAbortRef = useRef(null); // AbortController of the running camera-list load

  // Load logs from localStorage and analysis history from Supabase on mount
  useEffect(() => {
    setCameraLogs(loadStoredCameraLogs());
  }, []);

  // Self-heal localStorage history on mount (drops "N/A" entries saved before
  // the fix) and stop a running upload when leaving the page
  useEffect(() => {
    loadStoredUploadHistory();
    return () => {
      if (uploadAbortRef.current) uploadAbortRef.current.abort();
    };
  }, []);

  // The read-only Farmer ID box of the Add Camera form shows the id the form
  // will send for the farm chosen in it: the farm's registered farmer id when
  // the middleware has a farm record, else the logged-in user
  useEffect(() => {
    let cancelled = false;
    setAddCameraFarmerId(''); // never another farm's id while this one is looked up
    resolveFarmerId(addCameraFarmId, user?.id || '').then((farmerId) => {
      if (!cancelled) setAddCameraFarmerId(farmerId);
    });
    return () => { cancelled = true; };
  }, [addCameraFarmId, user?.id]);

  // Which records the Analysis History tab asks for and shows:
  //  - one farm chosen: the records of that farm
  //  - "All Farms" of a chosen organisation: the records of its farms
  //  - "All Farms": no farm filter (null). The database access rule decides,
  //    so the records saved without a farm are included for whoever may read
  //    them (today that rule shows them to admins only)
  const historyFarmIds = Boolean(selectedFarmId) && selectedFarmId !== 'all'
    ? [selectedFarmId]
    : Boolean(selectedOrgId) && selectedOrgId !== 'all'
      ? farms.map(f => f.id).filter(id => id)
      : null;
  const historyFarmKey = historyFarmIds ? historyFarmIds.join(',') : 'all';

  // Load analysis history from Supabase when farms are loaded and whenever
  // the farm selection changes
  useEffect(() => {
    let cancelled = false; // a newer selection took over
    const loadAnalysisHistory = async () => {
      if (farmsLoading) return;
      
      console.log('📊 Loading analysis history from Supabase...');
      // An organisation without farms has no records to ask for
      const result = historyFarmIds && historyFarmIds.length === 0
        ? { success: true, data: [] }
        : await havvaAnalysisService.getAnalysisHistory(historyFarmIds);
      if (cancelled) return;
      
      if (result.success) {
        // Plus the results kept only in this browser because the database
        // save failed; the list on screen filters them by farm like the rest
        setUploadHistory(mergeBackupHistory(normalizeHavvaHistory(result.data), loadStoredUploadHistory()));
        console.log('✅ Loaded', result.data.length, 'analysis records from Supabase');
      } else {
        console.error('❌ Failed to load analysis history:', result.error);
        // Fallback to localStorage if Supabase fails (normalised, "N/A" entries dropped)
        const storedHistory = loadStoredUploadHistory();
        if (storedHistory.length > 0) {
          setUploadHistory(storedHistory);
          console.log('⚠️ Using localStorage fallback');
        }
      }
    };
    
    loadAnalysisHistory();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmsLoading, farms.length, historyFarmKey]);

  // Helper function to save analysis result to Supabase
  // Only real analyses are saved: a result whose fields are all "N/A" is refused.
  const saveToHistory = async (analysisResult, fileName, farmId = null) => {
    const analysis = normalizeHavvaAnalysis(analysisResult);
    if (!analysis.hasAnalysis) {
      console.warn('[HAVVA] Not saving to history - no analysis values for', analysis.id);
      return false;
    }

    console.log('💾 Saving analysis to Supabase...', { analysisId: analysis.id, farmId });
    
    // Save to Supabase (time-boxed; a timeout falls back to localStorage below).
    // The service stores a new-format result whole in analysis_json when that
    // column exists, and always keeps its recommended checks / summary as
    // text in recommended_activity, so the record is never saved empty.
    const result = await withTimeout(
      havvaAnalysisService.saveAnalysis(analysis, farmId, fileName),
      HISTORY_SAVE_TIMEOUT_MS,
      { success: false, error: 'Saving to Supabase timed out' }
    );

    // The entry kept in state (and in localStorage when Supabase fails) is the
    // whole normalised result, new-format fields included
    const historyEntry = normalizeHavvaAnalysis({
      ...analysis,
      id: analysis.id || Date.now().toString(),
      farmId: farmId,
      timeStamp: analysis.timeStamp || new Date().toISOString(),
      fileName: fileName,
      savedAt: new Date().toISOString()
    });
    
    if (result.success) {
      console.log('✅ Analysis saved to Supabase');
      // Add to local state immediately for instant UI update
      setUploadHistory(prev => [{ ...historyEntry, dbId: result.data?.id }, ...prev]);
    } else {
      console.error('❌ Failed to save to Supabase:', result.error);
      // Fallback to localStorage
      setUploadHistory(prev => {
        const newHistory = [historyEntry, ...prev].slice(0, 50);
        writeStorage(UPLOAD_HISTORY_KEY, JSON.stringify(newHistory));
        return newHistory;
      });
    }
    return true;
  };

  // Helper function to add log entry
  const addLog = (cameraId, operation, request, response, status) => {
    const logEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      operation,
      request,
      response,
      status, // 'success' or 'error'
      httpStatus: response?.status || response?.httpStatus || null
    };

    setCameraLogs(prev => {
      const newLogs = {
        ...prev,
        [cameraId]: [...(prev[cameraId] || []), logEntry]
      };
      // Save to localStorage
      writeStorage(CAMERA_LOGS_KEY, JSON.stringify(newLogs));
      return newLogs;
    });
  };

  // Load cameras when farms are loaded and whenever the farm selection changes
  useEffect(() => {
    const farmSelected = Boolean(selectedFarmId) && selectedFarmId !== 'all';
    if (farmsLoading || (!farmSelected && farms.length === 0)) {
      // A load cut short by the cleanup below no longer clears the spinner itself
      setIsLoading(false);
      return undefined;
    }
    loadCameras();
    // Leaving the page or changing the selection stops the requests still
    // queued and drops the ones on their way
    return () => {
      cameraLoadRef.current += 1;
      if (cameraLoadAbortRef.current) cameraLoadAbortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmsLoading, farms.length, selectedFarmId]);

  // Track which farm+farmer pairs have cameras registered (persisted in localStorage)
  const getTrackedCameraFarms = () => {
    try {
      const saved = localStorage.getItem('havva_camera_farms');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed.filter(t => t && typeof t === 'object') : [];
    } catch { return []; }
  };
  
  const trackCameraFarm = (farmId, farmerId) => {
    const tracked = getTrackedCameraFarms();
    const key = `${farmId}__${farmerId}`;
    if (!tracked.find(t => `${t.farmId}__${t.farmerId}` === key)) {
      tracked.push({ farmId, farmerId });
      writeStorage('havva_camera_farms', JSON.stringify(tracked));
    }
  };

  // Load the camera list (API 2) for the current farm selection.
  //  - A farm is selected: only that farm is asked, under the farmer id of its
  //    middleware farm record (see farmIdentity) - or, when it has none, the
  //    logged-in user and 'F001' - plus any farmer id a camera of that farm
  //    was registered with here.
  //  - "All farms": the tracked registrations plus the first 10 farms, two
  //    requests at a time, each answer reused from memory for 5 minutes
  //    (plus, at no cost, any farm selected within those 5 minutes).
  // force: true (the Refresh button) asks the middleware for every pair.
  // After add / edit / delete the caller first drops the cached answers of the
  // farms involved (havvaService.invalidateCameraCache), so those are asked again.
  // A new load takes over from the one before it, whose requests are dropped.
  const loadCameras = async ({ force = false } = {}) => {
    const runId = ++cameraLoadRef.current;
    const isCurrent = () => runId === cameraLoadRef.current;
    if (cameraLoadAbortRef.current) cameraLoadAbortRef.current.abort();
    const loadController = new AbortController();
    cameraLoadAbortRef.current = loadController;
    setIsLoading(true);
    setError(null);
    try {
      // A load that is replaced straight away (a quick change of farm, the
      // page mounting twice in development) sends nothing
      await Promise.resolve();
      if (!isCurrent()) return;

      // Lists cached for another user (logout and login on the same tab) are forgotten
      havvaService.setCameraCacheOwner(user?.id);

      const allCameras = [];
      // Dedupe by the middleware's unique record id (cameraId alone collides
      // across farms — e.g. two farms can both have camera "2").
      const seenCameraIds = new Set();

      const addCameras = (data) => {
        if (data && Array.isArray(data)) {
          data.forEach(cam => {
            const key = cameraKey(cam);
            if (!seenCameraIds.has(key)) {
              seenCameraIds.add(key);
              allCameras.push(cam);
            }
          });
        }
      };

      // Farms this user can see (cached lists of other farms are never shown)
      const ownFarmIds = new Set(farms.map(f => String(f.id)));

      // Build unique farm+farmer queries. Farm ids are compared as text: a
      // tracked "5682" and the farm 5682 are one farm, not two requests.
      const farmSelected = Boolean(selectedFarmId) && selectedFarmId !== 'all';
      const trackedFarms = getTrackedCameraFarms();
      const queries = new Map(); // farmId -> Set of farmerIds
      const addQuery = (farmId, farmerId) => {
        if (farmId === null || farmId === undefined || farmId === '' || !farmerId) return;
        const key = String(farmId);
        if (!queries.has(key)) queries.set(key, new Set());
        queries.get(key).add(String(farmerId));
      };

      if (farmSelected) {
        // Only the selected farm, under the farmer id of its middleware farm
        // record: the pair a vendor publishes under, and the one Add Camera
        // registers with (see farmIdentity). Without a record: the logged-in
        // user's id, which Add Camera used until now, and the legacy 'F001'.
        const registeredFarmerId = await lookupRegisteredFarmerId(selectedFarmId);
        if (!isCurrent()) return;
        if (registeredFarmerId) {
          addQuery(selectedFarmId, registeredFarmerId);
        } else {
          addQuery(selectedFarmId, user?.id);
          addQuery(selectedFarmId, 'F001');
        }
        trackedFarms
          .filter(t => String(t.farmId) === String(selectedFarmId))
          .forEach(t => addQuery(t.farmId, t.farmerId));
      } else {
        // Tracked farms (previously registered cameras)
        trackedFarms.forEach(({ farmId, farmerId }) => addQuery(farmId, farmerId));

        // Also add first 10 context farms for discovery. Add Camera registers with
        // farmerId = the logged-in user's id, so query that too — localStorage
        // tracking alone made cameras invisible on any other device/browser.
        farms.slice(0, 10).forEach(f => {
          addQuery(f.id, 'F001');
          addQuery(f.id, user?.id);
        });

        // A manual refresh also asks again for a farm that was selected a
        // moment ago (see below), instead of repeating its cached answer
        if (force) {
          havvaService.getCachedCameraLists()
            .filter(entry => ownFarmIds.has(String(entry.farmId)))
            .forEach(entry => addQuery(entry.farmId, entry.farmerId));
        }
      }
      
      console.log('📷 [HAVVA] Loading cameras from', queries.size, 'farm queries');
      
      // Flatten into array of { farmId, farmerId } pairs
      const queryPairs = [];
      queries.forEach((farmerIds, farmId) => {
        farmerIds.forEach(farmerId => {
          queryPairs.push({ farmId, farmerId });
        });
      });
      
      // Two at a time. A selected farm is always asked afresh (two or three
      // requests); in the all-farms view an answer younger than 5 minutes is
      // reused unless this is a manual refresh. A load that has been
      // superseded stops asking.
      //
      // The middleware can hang without answering (every request then waits
      // out its 30 s timeout) or be unreachable. Once that shows - a request
      // timed out and nothing answered while it waited, or two requests in a
      // row could not get through - nothing more is asked in this load: the
      // pairs still queued are answered from memory where possible and
      // otherwise count as failed, so the cameras they returned last time are
      // kept and the "may be incomplete" message below is shown. (Waiting for
      // all 20 pairs of the all-farms view would take 5 minutes.)
      let lastAnswerAt = 0;    // when the middleware last answered a request
      let unreachableInARow = 0;
      let stopAsking = false;
      const results = await runWithConcurrency(queryPairs, CAMERA_LIST_CONCURRENCY, async (q) => {
        if (!isCurrent()) return null;
        const startedAt = Date.now();
        const r = await havvaService.listCamerasCached(q.farmId, q.farmerId, {
          force: force || farmSelected,
          cacheOnly: stopAsking,
          signal: loadController.signal
        });
        if (r.cached || r.code === 'skipped' || r.code === 'cancelled') return r;
        if (r.code === 'timeout' || r.code === 'network') {
          unreachableInARow += 1;
          if ((r.code === 'timeout' && lastAnswerAt < startedAt) || unreachableInARow >= 2) stopAsking = true;
        } else {
          // Any answer, even an HTTP error, shows the middleware is there
          lastAnswerAt = Date.now();
          unreachableInARow = 0;
        }
        return r;
      });
      if (!isCurrent()) return;

      const failures = [];
      const failedPairs = new Set(); // "farmId__farmerId" of every query that failed
      results.forEach((r, index) => {
        if (r && r.success) {
          addCameras(r.data);
        } else {
          failures.push(r || {});
          failedPairs.add(`${queryPairs[index].farmId}__${queryPairs[index].farmerId}`);
        }
      });

      // "All farms" also keeps the cameras of a farm that was selected a
      // moment ago (it may lie outside the first 10): its answer is still in
      // memory, so this costs no request. Only farms this user can see.
      if (!farmSelected) {
        havvaService.getCachedCameraLists()
          .filter(entry => ownFarmIds.has(String(entry.farmId)))
          .forEach(entry => addCameras(entry.data));
      }
      
      console.log('📷 [HAVVA] Total cameras loaded:', allCameras.length);
      if (queryPairs.length > 0 && failures.length === queryPairs.length) {
        // Every query failed (middleware down, timeout, or an HTTP-200
        // {"message": ...} body): say so and keep the list we already had,
        // instead of pretending there are no cameras
        setError(`Could not load cameras. ${failures[0].message || ''}`.trim());
      } else if (failures.length > 0) {
        // Some queries failed: their cameras are unknown, not gone. Keep the
        // ones those queries returned last time and say the list may be
        // incomplete, instead of silently showing fewer (or no) cameras.
        setCameras(prev => [
          ...allCameras,
          ...prev.filter(cam =>
            failedPairs.has(`${cam.farmId}__${cam.farmerId}`) &&
            !seenCameraIds.has(cameraKey(cam))
          )
        ]);
        setError(
          `Some cameras could not be loaded (${failures.length} of ${queryPairs.length} requests failed), ` +
          `so this list may be incomplete. ${failures[0].message || ''}`.trim()
        );
      } else {
        setCameras(allCameras);
      }
    } catch (err) {
      if (!isCurrent()) return;
      setError('Error loading cameras');
      console.error(err);
    } finally {
      // A superseded load leaves the spinner to the load that replaced it
      if (isCurrent()) setIsLoading(false);
    }
  };

  // Filter cameras based on selected farm from context
  const filteredCameras = selectedFarmId === 'all' ? cameras : cameras.filter(camera => String(camera.farmId) === String(selectedFarmId));

  // Detail Analysis tab: the chosen camera, as long as it is still in the
  // list. After a change of farm (or once the camera is deleted) the select
  // is back on "Choose a camera..." and nothing of the old choice is shown.
  const shownDetailCamera = detailCamera && filteredCameras.some(c => cameraKey(c) === cameraKey(detailCamera))
    ? detailCamera
    : null;

  // Analysis History tab: the loaded records of the chosen farm (or of the
  // chosen organisation's farms); in the all-farms view every loaded record,
  // those without a farm included.
  const historyFarmSet = historyFarmIds ? new Set(historyFarmIds.map(toStoredFarmId)) : null;
  const shownHistory = historyFarmSet
    ? uploadHistory.filter(item => toStoredFarmId(item.farmId) !== null && historyFarmSet.has(toStoredFarmId(item.farmId)))
    : uploadHistory;

  // device-list currently returns only id, cameraId, note, farmerId, farmId, vendorId.
  // Show the status / quality / recommendation columns only when a camera has them.
  const showStatusColumn = filteredCameras.some(camera => toDisplayText(camera.cameraStatus));
  const showQualityColumn = filteredCameras.some(camera => toDisplayText(camera.cameraQuality));
  const showRecommendationColumn = filteredCameras.some(camera => toDisplayText(camera.recommendedActivity));
  
  // The most recent analysis the dashboard itself stored for a camera: from
  // the upload history already on the page, else from havva_analysis_history.
  // Camera ids repeat across farms, so the farm has to be the same as well: a
  // camera of farm A never shows an analysis of farm B, nor one stored without
  // a farm (a row loses its farm when that farm is deleted). A camera without
  // a farm gets only the entries stored without one. null when there is none.
  const findStoredAnalysis = async (camera) => {
    const farmId = toStoredFarmId(camera.farmId);
    const sameCamera = (item) =>
      String(item.cameraId) === String(camera.cameraId) && toStoredFarmId(item.farmId) === farmId;
    const newestFirst = (a, b) => (Date.parse(b.timeStamp) || 0) - (Date.parse(a.timeStamp) || 0);

    const loaded = normalizeHavvaHistory(uploadHistory)
      .filter(item => item.hasAnalysis && sameCamera(item))
      .sort(newestFirst);
    if (loaded.length > 0) return loaded[0];

    // The page holds only the latest 50 records of all farms; ask for this camera
    const result = await havvaAnalysisService.getAnalysisByCamera(camera.cameraId, { farmId, limit: 5 });
    if (!result.success) return null;
    return normalizeHavvaHistory(result.data).find(item => item.hasAnalysis && sameCamera(item)) || null;
  };

  // Get detail analysis for a camera (API 6)
  // Params: farmerId, farmId, vendorId, id (analysis id)
  // When the middleware has nothing usable for the camera (it answers {} for
  // every upload since 2026, or fails), the latest analysis stored by the
  // dashboard is shown instead.
  const getCameraDetailAnalysis = async (camera, analysisId) => {
    const requestId = ++detailRequestRef.current;
    setIsDetailLoading(true);
    setDetailAnalysis(null);
    setDetailStatus(null);
    setStoredDetail(null);
    try {
      const farmId = camera.farmId;
      // An existing record: under its own farmer id (the pair the middleware
      // listed it with), else the farm's registered id / the current user
      const farmerId = camera.farmerId || await resolveFarmerId(farmId, user?.id);
      const id = analysisId || camera.id;
      const result = await havvaService.getDetailAnalysis(farmerId, farmId, id);
      if (requestId !== detailRequestRef.current) return null; // a newer selection took over

      let status = null;
      if (result.success) {
        // Flat (live / V1) or nested (V2 document) - the normaliser reads
        // both. {} (what the middleware returns for every new upload) ends up
        // with no usable field.
        const detail = normalizeHavvaDetail(result.data);

        if (detail.hasDetail) {
          setDetailAnalysis(detail);
          return detail;
        }
        status = { type: 'unavailable' };
      } else {
        status = { type: 'error', message: result.message };
      }

      // Nothing usable from the middleware: fall back to the upload history
      const stored = await findStoredAnalysis(camera).catch((lookupError) => {
        console.warn('[HAVVA] Could not look up the stored analysis:', lookupError);
        return null;
      });
      if (requestId !== detailRequestRef.current) return null;
      if (stored) {
        setStoredDetail(stored);
        return null;
      }
      setDetailStatus(status);
    } catch (err) {
      console.error('Error fetching detail analysis:', err);
      if (requestId === detailRequestRef.current) {
        setDetailStatus({ type: 'error', message: err.message });
      }
    } finally {
      // A stale request leaves the flag alone: whoever superseded it (a newer
      // selection, or the deselect branch of the camera <select>) owns it now
      if (requestId === detailRequestRef.current) setIsDetailLoading(false);
    }
    return null;
  };
  
  // Get analysis history for camera (API 5)
  // Params: farmerId, farmId, vendorId
  const loadAnalysisHistory = async (camera) => {
    setIsLoading(true);
    try {
      const farmId = camera.farmId;
      // An existing record: under its own farmer id, else the farm's
      // registered id / the current user
      const farmerId = camera.farmerId || await resolveFarmerId(farmId, user?.id);
      const result = await havvaService.getAnalysisHistory(farmerId, farmId);
      if (result.success && result.data) {
        setAnalysisHistory(result.data);
      }
    } catch (err) {
      console.error('Error fetching analysis history:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (camera) => {
    setSelectedCamera(camera);
    setIsDetailModelOpen(true);
  };

  const handleEditCamera = (camera) => {
    setSelectedCamera(camera);
    setIsEditCameraModalOpen(true);
  };

  const handleDeleteCamera = async (camera) => {
    if (!window.confirm('Are you sure you want to delete this camera?')) return;
    
    setIsSubmitting(true);
    const deleteData = {
      id: camera.id,
      // The camera's own farmer id (the pair the middleware listed it with;
      // a delete moves nothing), else the farm's registered id / the user
      farmerId: camera.farmerId || await resolveFarmerId(camera.farmId, user?.id),
      farmId: camera.farmId,
      vendorId: camera.vendorId || process.env.REACT_APP_HAVVA_VENDOR_ID
    };
    
    try {
      const result = await havvaService.deleteCamera(deleteData);
      // Whatever the answer, the cached camera list of this farm is no longer trusted
      havvaService.invalidateCameraCache([camera.farmId]);
      
      if (result.success) {
        // Add log entry
        addLog(camera.cameraId || camera.id, 'DELETE', deleteData, result.data, 'success');
        
        setFeedback({ type: 'success', message: 'Camera deleted successfully!' });
        // Drop it from the list first: if part of the refresh fails, the
        // cameras of the failed queries are kept, and this one must not be
        const deletedKey = cameraKey(camera);
        setCameras(prev => prev.filter(c => cameraKey(c) !== deletedKey));
        await loadCameras(); // Refresh list
        setTimeout(() => setFeedback(null), 3000);
      } else {
        // Add error log
        addLog(camera.cameraId || camera.id, 'DELETE', deleteData, result, 'error');
        setFeedback({ type: 'error', message: result.message || 'Failed to delete camera' });
      }
    } catch (err) {
      // Add error log
      addLog(camera.cameraId || camera.id, 'DELETE', deleteData, { error: err.message }, 'error');
      havvaService.invalidateCameraCache([camera.farmId]);
      setFeedback({ type: 'error', message: 'Error deleting camera' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEditCamera = async (updatedCamera) => {
    setIsSubmitting(true);
    const editData = {
      id: updatedCamera.id,
      cameraId: updatedCamera.cameraId,
      note: updatedCamera.note,
      // The farm's registered farmer id (its middleware record), else what the form says
      farmerId: await resolveFarmerId(updatedCamera.farmId, updatedCamera.farmerId),
      farmId: updatedCamera.farmId,
      vendorId: updatedCamera.vendorId || process.env.REACT_APP_HAVVA_VENDOR_ID
    };
    
    // An edit can move the camera to another farm: both lists change
    const editedFarmIds = [selectedCamera?.farmId, updatedCamera.farmId];

    try {
      const result = await havvaService.editCamera(editData);
      // Whatever the answer, the cached camera lists of these farms are no longer trusted
      havvaService.invalidateCameraCache(editedFarmIds);
      
      if (result.success) {
        // Add log entry
        addLog(updatedCamera.cameraId, 'EDIT', editData, result.data, 'success');

        // Keep asking for this farm+farmer pair, so the camera stays in the
        // list when its farm or farmer id was changed
        trackCameraFarm(editData.farmId, editData.farmerId);
        
        setFeedback({ type: 'success', message: 'Camera updated successfully!' });
        await loadCameras(); // Refresh list
        setIsEditCameraModalOpen(false);
        setTimeout(() => setFeedback(null), 3000);
      } else {
        // Add error log
        addLog(updatedCamera.cameraId, 'EDIT', editData, result, 'error');
        setFeedback({ type: 'error', message: result.message || 'Failed to update camera' });
      }
    } catch (err) {
      // Add error log
      addLog(updatedCamera.cameraId, 'EDIT', editData, { error: err.message }, 'error');
      havvaService.invalidateCameraCache(editedFarmIds);
      setFeedback({ type: 'error', message: 'Error updating camera' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'status-good';
      case 'Alert': return 'status-fault';
      default: return 'status-warning';
    }
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'High': return 'text-green-600 bg-green-50';
      case 'Medium': return 'text-yellow-600 bg-yellow-50';
      case 'Low': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Overall score from the legacy detail metrics; null when none of them is a number
  const getHealthScore = () => {
    if (!detailAnalysis) return null;

    const metrics = [
      detailAnalysis.leafDamage,
      detailAnalysis.diseaseSpreadRate,
      detailAnalysis.plantHeightVariation,
      detailAnalysis.leafColorDeviation,
      detailAnalysis.growthRateReduction
    ].map(parsePercentage);

    if (metrics.every(value => value === null)) return null;

    const totalDamage = metrics.reduce((sum, value) => sum + (value || 0), 0);
    return Math.max(0, 100 - totalDamage);
  };

  // Cancel the running upload (only possible while waiting for the middleware)
  const handleCancelUpload = () => {
    if (uploadAbortRef.current) uploadAbortRef.current.abort();
  };

  // Upload Image (API 7): send the photo, then decide between
  // "real analysis" (save + history) and "no analysis" (nothing is stored)
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (uploadAbortRef.current) return; // an upload is already running

    const form = e.target;
    const formData = new FormData(form);
    // The select carries the camera's record key: a camera id alone can
    // belong to two farms in the all-farms view
    const selectedCam = filteredCameras.find(c => cameraKey(c) === formData.get('camera'));
    const cameraId = String(selectedCam ? selectedCam.cameraId : formData.get('camera'));
    const farmId = formData.get('farmId');

    // Use the selectedFile state instead of form data
    if (!selectedFile) {
      setUploadResult({ type: 'error', message: 'Please select an image file' });
      return;
    }
    const file = selectedFile;

    const controller = new AbortController();
    uploadAbortRef.current = controller;
    setIsSubmitting(true);
    setUploadPhase('analyzing');
    setUploadResult(null);

    try {
      const uploadFarmId = farmId || selectedCam?.farmId;
      // The selected camera's own farmer id (the pair the middleware listed
      // it with), else the farm's registered id / the current user
      const farmerId = selectedCam?.farmerId || await resolveFarmerId(uploadFarmId, user?.id);
      const logRequest = { cameraId, farmerId, farmId: uploadFarmId, fileName: file.name };

      console.log('[HAVVA] Uploading image:', file.name, 'farmerId:', farmerId, 'farmId:', uploadFarmId);

      // Call the real middleware API (POST multipart/form-data, 120 s timeout, cancellable)
      const result = await havvaService.uploadImage(file, farmerId, uploadFarmId, { signal: controller.signal });

      if (!result.success) {
        // result.message already distinguishes timeout / cancelled / HTTP 5xx / network
        addLog(cameraId, 'UPLOAD', logRequest, { message: result.message, code: result.code, httpStatus: result.httpStatus }, 'error');
        setUploadResult({
          type: result.code === 'cancelled' ? 'cancelled' : 'error',
          message: result.message || 'Failed to upload image'
        });
        return;
      }

      const analysis = normalizeHavvaAnalysis(result.data);

      // HTTP 200 with a real id but every analysis field "N/A": the photo
      // arrived, HAVVA analysed nothing. Not a success - nothing is saved to
      // Supabase, history state or localStorage, and the tab stays put.
      if (!analysis.hasAnalysis) {
        addLog(cameraId, 'UPLOAD', logRequest, result.data, 'error');
        if (analysis.id === null) {
          // No id either (e.g. HTTP 200 {"message": "..."}): there is no sign
          // that HAVVA received the photo, so do not claim it did
          const vendorMessage = toDisplayText(result.data?.error?.message) || toDisplayText(result.data?.message);
          setUploadResult({
            type: 'error',
            message: vendorMessage
              ? `The analysis service returned an unexpected response: ${vendorMessage}`
              : 'The analysis service returned an unexpected response. Please try again later.'
          });
          return;
        }
        setUploadResult({ type: 'no-analysis', id: analysis.id });
        return;
      }

      // Add log entry
      addLog(cameraId, 'UPLOAD', logRequest, result.data, 'success');
      setUploadPhase('saving');

      // HAVVA's annotated image is never kept by its own address when that
      // address is signed: the new format's URL (kapitani-api.havva.my/...
      // ?expires=&signature=) stops working 24 hours after the job. So the
      // image is downloaded here and a copy is stored next to the original
      // photo. If the browser cannot read it (CORS, 401/404 - the old
      // sirim.havva.my/uploads URLs are often dead on arrival - or no answer
      // in time), the copy of the original photo is used instead, as before.
      // A data: URI is displayable but far too large for a history row or
      // localStorage, so it is stored as a file in the same way.
      const vendorImage = analysis.annotatedImage;
      const annotated = (isHttpUrl(vendorImage) || isImageDataUri(vendorImage))
        ? await fetchImageBlob(vendorImage, controller.signal)
        : null;
      // A plain (unsigned) URL that serves the image may stand in if our copy
      // cannot be stored. A signed URL never does.
      let imageUrl = annotated && isHttpUrl(vendorImage) && !isSignedUrl(vendorImage) ? vendorImage : null;
      try {
        const timestamp = Date.now();
        const fileExt = file.name.split('.').pop();
        const filePath = `havva/${cameraId}/${timestamp}.${fileExt}`;
        const { error: storageError } = await withTimeout(
          supabase.storage
            .from('images')
            .upload(filePath, file, { cacheControl: '3600', upsert: false }),
          STORAGE_UPLOAD_TIMEOUT_MS,
          { error: new Error('Storage upload timed out') }
        );
        let originalUrl = null;
        if (!storageError) {
          const { data: urlData } = supabase.storage.from('images').getPublicUrl(filePath);
          originalUrl = urlData?.publicUrl || null;
        }
        let annotatedCopyUrl = null;
        if (annotated) {
          const annotatedPath = `havva/${cameraId}/${timestamp}-annotated.${annotated.ext}`;
          const { error: annotatedError } = await withTimeout(
            supabase.storage
              .from('images')
              .upload(annotatedPath, annotated.blob, { cacheControl: '3600', upsert: false, contentType: annotated.type }),
            STORAGE_UPLOAD_TIMEOUT_MS,
            { error: new Error('Storage upload timed out') }
          );
          if (!annotatedError) {
            const { data: annotatedUrlData } = supabase.storage.from('images').getPublicUrl(annotatedPath);
            annotatedCopyUrl = annotatedUrlData?.publicUrl || null;
          }
        }
        imageUrl = annotatedCopyUrl || imageUrl || originalUrl;
      } catch (storageErr) {
        console.warn('[HAVVA] Supabase storage upload failed (non-critical):', storageErr);
      }

      // Save to Supabase analysis history
      const analysisForHistory = normalizeHavvaAnalysis({
        ...analysis,
        cameraId,
        annotatedImage: imageUrl
      });
      await saveToHistory(analysisForHistory, file.name, toStoredFarmId(uploadFarmId));

      // Stay on this tab; the result panel offers a "View in History" button
      setUploadResult({
        type: 'success',
        id: analysis.id,
        healthyPercentage: analysis.healthyPercentage,
        unhealthyPercentage: analysis.unhealthyPercentage,
        analysis: analysisForHistory // normalised; the panel shows its new-format result
      });
      form.reset();
      setSelectedFile(null);
      setFilePreview(null);
    } catch (err) {
      addLog(cameraId, 'UPLOAD', { cameraId, fileName: file.name }, { error: err.message }, 'error');
      setUploadResult({ type: 'error', message: 'Error uploading image: ' + err.message });
    } finally {
      uploadAbortRef.current = null;
      setUploadPhase(null);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Feedback Message */}
      {feedback && (
        <div className={`p-4 rounded-lg ${
          feedback.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm font-medium ${
            feedback.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {feedback.message}
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">HAVVA</h1>
        <div className="flex items-center space-x-4">
          {/* Organization Selector - Admin Only */}
          {hasOrganizations && <OrganizationSelector />}
          {/* Farm Selector */}
          {hasFarms && <FarmSelector />}
        </div>
      </div>

      {/* Tab Navigation - Based on 7 API Endpoints */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'list', name: 'List Cameras', icon: ListBulletIcon },
          { id: 'add', name: 'Add Camera', icon: PlusIcon },
          { id: 'detail', name: 'Detail Analysis', icon: MagnifyingGlassIcon },
          { id: 'history', name: 'Analysis History', icon: CalendarIcon },
          { id: 'upload', name: 'Upload Image', icon: DocumentArrowUpIcon },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
              selectedTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Content based on selected tab */}
      
      {/* List Cameras Tab - API 2 */}
      {selectedTab === 'list' && (
        <div className="card overflow-hidden">
          {/* Manual refresh: asks the middleware again instead of reusing the cached list */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">
              {isLoading ? 'Loading cameras...' : `${filteredCameras.length} camera${filteredCameras.length !== 1 ? 's' : ''}`}
            </span>
            <button
              type="button"
              onClick={() => loadCameras({ force: true })}
              disabled={isLoading || isSubmitting}
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reload the camera list from HAVVA"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {isLoading && (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading cameras...</p>
            </div>
          )}
          
          {!isLoading && cameras.length === 0 && !error && (
            <div className="p-8 text-center">
              <CameraIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">No cameras found. Add a camera to get started.</p>
            </div>
          )}
          
          {!isLoading && cameras.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Camera ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Farm
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  {showStatusColumn && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {showQualityColumn && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quality
                    </th>
                  )}
                  {showRecommendationColumn && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recommended Activity
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCameras.map((camera) => (
                  <tr key={cameraKey(camera)} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CameraIcon className="h-5 w-5 text-blue-500 mr-3" />
                        <div className="text-sm font-medium text-gray-900">{camera.cameraId}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        const farm = farms.find(f => String(f.id) === String(camera.farmId));
                        return farm ? farm.farmName || farm.name : camera.farmId || '-';
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {camera.note}
                    </td>
                    {showStatusColumn && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {toDisplayText(camera.cameraStatus) ? (
                          <span className={`status-badge ${getStatusColor(camera.cameraStatus)}`}>
                            {toDisplayText(camera.cameraStatus)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    {showQualityColumn && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {toDisplayText(camera.cameraQuality) ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getQualityColor(camera.cameraQuality)}`}>
                            {toDisplayText(camera.cameraQuality)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    {showRecommendationColumn && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {toDisplayText(camera.recommendedActivity) || '—'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(camera)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditCamera(camera)}
                          className="text-green-600 hover:text-green-900"
                          title="Edit Camera"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCamera(camera)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Camera"
                          disabled={isSubmitting}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCameraForLog(camera);
                            setLogPage(1); // Reset to first page when opening
                            setShowLogModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 relative"
                          title="View API Logs"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                          {cameraLogs[camera.cameraId]?.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                              {cameraLogs[camera.cameraId].length}
                            </span>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Add Camera Tab - API 1 */}
      {selectedTab === 'add' && (
        <div className="max-w-2xl mx-auto">
          <div className="card">
            <div className="text-center mb-6">
              <CameraIcon className="mx-auto h-12 w-12 text-blue-500" />
              <h3 className="text-xl font-semibold text-gray-900 mt-3">Add New Camera</h3>
              <p className="text-sm text-gray-500 mt-2">Register a new camera to the HAVVA system</p>
            </div>
            
            <form className="space-y-6" onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              
              const formData = new FormData(e.target);
              const farmId = addCameraFarmId || formData.get('farmId');
              const cameraData = {
                cameraId: formData.get('cameraId'),
                note: formData.get('note'),
                // The farm's registered farmer id (its middleware record), else the logged-in user
                farmerId: await resolveFarmerId(farmId, user?.id || formData.get('farmerId')),
                farmId,
                vendorId: havvaVendorId || formData.get('vendorId')
              };
              
              try {
                const result = await havvaService.registerCamera(cameraData);
                // Whatever the answer, the cached camera list of this farm is no longer trusted
                havvaService.invalidateCameraCache([cameraData.farmId]);
                
                if (result.success) {
                  // Add log entry
                  const cameraId = result.data?.cameraId || cameraData.cameraId;
                  addLog(cameraId, 'REGISTER', cameraData, result.data, 'success');
                  
                  // Track this farm+farmer pair for future camera loading
                  trackCameraFarm(cameraData.farmId, cameraData.farmerId);
                  
                  setFeedback({ type: 'success', message: 'Camera registered successfully!' });
                  await loadCameras(); // Refresh list
                  e.target.reset();
                  setAddCameraFarmId('');
                  setSelectedTab('list');
                  setTimeout(() => setFeedback(null), 3000);
                } else {
                  // Add error log
                  addLog(cameraData.cameraId, 'REGISTER', cameraData, result, 'error');
                  setFeedback({ type: 'error', message: result.message || 'Failed to register camera' });
                }
              } catch (err) {
                // Add error log
                addLog(cameraData.cameraId, 'REGISTER', cameraData, { error: err.message }, 'error');
                havvaService.invalidateCameraCache([cameraData.farmId]);
                setFeedback({ type: 'error', message: 'Error registering camera' });
              } finally {
                setIsSubmitting(false);
              }
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Farmer ID - Auto-filled from current user, not editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Farmer ID
                    <LockClosedIcon className="h-4 w-4 ml-1 text-gray-400" title="Auto-filled" />
                  </label>
                  <input 
                    type="text" 
                    name="farmerId"
                    value={addCameraFarmerId || user?.id || ''}
                    className="input bg-gray-100 cursor-not-allowed"
                    readOnly
                    title="Auto-filled from your account"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from your account</p>
                </div>

                {/* Vendor ID - Auto-filled from config, not editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Vendor ID
                    <LockClosedIcon className="h-4 w-4 ml-1 text-gray-400" title="Auto-filled" />
                  </label>
                  <input 
                    type="text" 
                    name="vendorId"
                    value={havvaVendorId}
                    className="input bg-gray-100 cursor-not-allowed"
                    readOnly
                    title="Auto-filled from system configuration"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from system config</p>
                </div>

                {/* Farm ID - Searchable dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Farm ID *
                  </label>
                  <SearchableFarmSelect
                    farms={farms}
                    value={addCameraFarmId}
                    onChange={(val) => setAddCameraFarmId(val)}
                    name="farmId"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Select from your registered farms</p>
                </div>

                {/* Camera ID - User input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Camera ID *
                  </label>
                  <input 
                    type="text" 
                    name="cameraId"
                    className="input"
                    placeholder="e.g., CAM005"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note *
                </label>
                <textarea 
                  name="note"
                  className="input"
                  rows="3"
                  placeholder="Describe the camera location and purpose..."
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">API Response Format</h4>
                <div className="text-xs text-blue-700 space-y-1">
                  <div>• <strong>status:</strong> Success/Failure status</div>
                  <div>• <strong>id:</strong> Generated camera ID</div>
                  <div>• <strong>timeStamp:</strong> Registration timestamp</div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setSelectedTab('list')}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Camera'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Analysis Tab - API 6 */}
      {selectedTab === 'detail' && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Detail Analysis</h2>
            <div className="text-sm text-gray-500">Select a camera to view detailed analysis</div>
          </div>

          {/* Camera Selection */}
          <div className="card">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Camera for Detail Analysis
            </label>
            <select 
              className="input"
              value={shownDetailCamera ? cameraKey(shownDetailCamera) : ''}
              onChange={async (e) => {
                const camera = filteredCameras.find(c => cameraKey(c) === e.target.value) || null;
                setDetailCamera(camera);
                if (camera) {
                  await getCameraDetailAnalysis(camera);
                } else {
                  // Supersedes any request still running, so its spinner must
                  // be cleared here - the stale request will not do it
                  detailRequestRef.current += 1;
                  setIsDetailLoading(false);
                  setDetailAnalysis(null);
                  setDetailStatus(null);
                  setStoredDetail(null);
                }
              }}
            >
              <option value="">Choose a camera...</option>
              {filteredCameras.map(camera => (
                <option key={cameraKey(camera)} value={cameraKey(camera)}>
                  {camera.cameraId} - {camera.note}
                </option>
              ))}
            </select>
          </div>

          {/* Loading State */}
          {isDetailLoading && shownDetailCamera && (
            <div className="card text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Loading analysis...</p>
            </div>
          )}

          {/* Nothing usable from the middleware (it answers {} for every new
              upload): the latest analysis stored by the dashboard for this camera */}
          {!isDetailLoading && shownDetailCamera && !detailAnalysis && storedDetail && (
            <div className="card" data-testid="havva-stored-detail">
              <p className="text-xs text-gray-500 mb-4">Latest stored analysis (from upload history)</p>
              <HavvaAnalysisDetails analysis={storedDetail} />
            </div>
          )}

          {/* No usable detail and nothing stored for this camera either */}
          {!isDetailLoading && shownDetailCamera && !detailAnalysis && !storedDetail && detailStatus?.type === 'unavailable' && (
            <div className="card text-center py-8" role="status">
              <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-700">
                Detail analysis is not available for this record.
              </p>
            </div>
          )}

          {/* Request failed (timeout, HTTP error, network) */}
          {!isDetailLoading && shownDetailCamera && !detailAnalysis && !storedDetail && detailStatus?.type === 'error' && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200" role="alert">
              <p className="text-sm font-medium text-red-800">
                Could not load the detail analysis. {detailStatus.message}
              </p>
            </div>
          )}

          {/* Detailed Analysis Results - API 6 Format */}
          {!isDetailLoading && shownDetailCamera && detailAnalysis && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Analysis Image */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Image</h3>
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    {detailAnalysis.annotatedImage ? (
                      <img 
                        src={detailAnalysis.annotatedImage} 
                        alt="Plant Analysis"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="text-center">
                        <CameraIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-xs text-gray-500">No image returned</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 text-sm text-gray-500">
                    <div className="flex justify-between">
                      <span>Timestamp:</span>
                      <span className="font-medium">{formatHavvaDate(detailAnalysis.timeStamp, true)}</span>
                    </div>
                  </div>
                </div>

                {/* Detailed Metrics */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Health Metrics</h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Leaf Damage', value: detailAnalysis.leafDamage, unit: '%' },
                      { label: 'Disease Spread Rate', value: detailAnalysis.diseaseSpreadRate, unit: '%' },
                      { label: 'Plant Height Variation', value: detailAnalysis.plantHeightVariation, unit: '%' },
                      { label: 'Leaf Color Deviation', value: detailAnalysis.leafColorDeviation, unit: '%' },
                      { label: 'Growth Rate Reduction', value: detailAnalysis.growthRateReduction, unit: '%' }
                    ].map(metric => {
                      // Legacy metrics are text: usually a number, sometimes missing
                      const numericValue = parsePercentage(metric.value);
                      const hasUnit = String(metric.value ?? '').trim().endsWith(metric.unit);
                      return (
                        <div key={metric.label} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{metric.label}</span>
                          <div className="flex items-center space-x-2">
                            <span className={`text-sm font-medium px-2 py-1 rounded ${
                              numericValue === null ? 'bg-gray-100 text-gray-600' :
                              numericValue <= 5 ? 'bg-green-100 text-green-800' :
                              numericValue <= 15 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {metric.value === null || metric.value === undefined
                                ? '—'
                                : `${metric.value}${numericValue !== null && !hasUnit ? metric.unit : ''}`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Overall Health Score */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-base font-medium text-gray-900">Overall Health Score</span>
                      <span className={`text-lg font-bold px-3 py-1 rounded ${
                        getHealthScore() === null ? 'bg-gray-100 text-gray-600' :
                        getHealthScore() >= 80 ? 'bg-green-100 text-green-800' :
                        getHealthScore() >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {getHealthScore() === null ? '—' : `${Number(getHealthScore().toFixed(1))}%`}
                      </span>
                    </div>
                  </div>

                  {/* Recommended Activity */}
                  {detailAnalysis.recommendedActivity && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-900 mb-1">Recommended Activity</h4>
                      <p className="text-sm text-blue-800">{detailAnalysis.recommendedActivity}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* New-format result, should the middleware ever return one here */}
              {detailAnalysis.isNewFormat && (
                <div className="card">
                  <HavvaNewFormatResult analysis={detailAnalysis} />
                </div>
              )}
            </>
          )}

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-orange-900 mb-2">API Response Format</h4>
            <div className="text-xs text-orange-700 space-y-1">
              <div>• <strong>cameraId:</strong> Camera identifier</div>
              <div>• <strong>timeStamp:</strong> Analysis timestamp</div>
              <div>• <strong>leafDamage, diseaseSpreadRate, etc.:</strong> Health metrics</div>
              <div>• <strong>recommendedActivity:</strong> Suggested action</div>
              <div>• <strong>image:</strong> Analysis image URL</div>
            </div>
          </div>
        </>
      )}

      {/* Analysis History Tab - Shows upload analysis results */}
      {selectedTab === 'history' && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Analysis History</h2>
            <div className="text-sm text-gray-500">
              {shownHistory.length} analysis record{shownHistory.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Empty State */}
          {shownHistory.length === 0 && (
            <div className="card text-center py-12">
              <CalendarIcon className="mx-auto h-16 w-16 text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No Analysis History</h3>
              <p className="mt-2 text-sm text-gray-500">
                Upload images in the "Upload Image" tab to see analysis results here.
              </p>
              <button
                onClick={() => setSelectedTab('upload')}
                className="mt-4 btn-primary"
              >
                Upload First Image
              </button>
            </div>
          )}

          {/* Analysis History Grid */}
          {shownHistory.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shownHistory.map((item, index) => (
                <div 
                  key={item.id || index} 
                  className="card hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedHistoryItem(item)}
                >
                  {/* Annotated Image */}
                  {item.annotatedImage && (
                    <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-lg">
                      <img 
                        src={item.annotatedImage} 
                        alt="Analysis result"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="12">No Image</text></svg>';
                        }}
                      />
                      {/* Health Badge - only when HAVVA returned a percentage */}
                      {parsePercentage(item.healthyPercentage) !== null && (
                        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold ${
                          item.healthyPercentage >= 70 ? 'bg-green-500 text-white' :
                          item.healthyPercentage >= 40 ? 'bg-yellow-500 text-white' :
                          'bg-red-500 text-white'
                        }`}>
                          {formatPercentage(item.healthyPercentage)} Healthy
                        </div>
                      )}
                    </div>
                  )}

                  {/* Card Content */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CameraIcon className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="font-medium text-gray-900">{item.cameraId}</span>
                        {/* A record saved without a farm */}
                        {toStoredFarmId(item.farmId) === null && (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">No farm</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatHavvaDate(item.timeStamp)}
                      </span>
                    </div>

                    {/* Health Bars - only when HAVVA returned real percentages
                        (old format). The new format has an AI confidence
                        instead, which is not a share of healthy plants. */}
                    {item.hasPercentages && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-green-600">Healthy</span>
                        <span className="font-medium">{formatPercentage(item.healthyPercentage)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${clampPercentage(item.healthyPercentage)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-red-600">Unhealthy</span>
                        <span className="font-medium">{formatPercentage(item.unhealthyPercentage)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full transition-all"
                          style={{ width: `${clampPercentage(item.unhealthyPercentage)}%` }}
                        />
                      </div>
                    </div>
                    )}

                    {/* New-format result: crop, health, growth, summary (details in the modal) */}
                    {item.isNewFormat && <HavvaNewFormatResult analysis={item} compact />}

                    {/* Recommendation (old format; a new-format result lists its checks) */}
                    {(!item.isNewFormat || item.recommendedActivity) && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {item.recommendedActivity || 'No recommendation available'}
                      </p>
                    </div>
                    )}

                    {/* File name */}
                    <div className="text-xs text-gray-400 truncate">
                      📁 {item.fileName}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Analysis Detail Modal */}
      {selectedHistoryItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Analysis Details</h3>
                <button
                  onClick={() => setSelectedHistoryItem(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Image, camera, time and the result - old or new format */}
              <HavvaAnalysisDetails analysis={selectedHistoryItem} />
            </div>
          </div>
        </div>
      )}

      {/* Upload Image Tab - API 7 */}
      {selectedTab === 'upload' && (
        <div className="max-w-2xl mx-auto">
          <div className="card">
            <div className="text-center mb-6">
              <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="text-xl font-semibold text-gray-900 mt-3">Upload Image for Analysis</h3>
              <p className="text-sm text-gray-500 mt-2">Upload plant images to HAVVA for AI analysis</p>
            </div>
            
            {/* Upload result: success / no analysis / cancelled / error */}
            {uploadResult && (
              <div
                className={`mb-6 p-4 rounded-lg border ${
                  uploadResult.type === 'success' ? 'bg-green-50 border-green-200' :
                  uploadResult.type === 'no-analysis' ? 'bg-amber-50 border-amber-300' :
                  uploadResult.type === 'cancelled' ? 'bg-gray-50 border-gray-200' :
                  'bg-red-50 border-red-200'
                }`}
                role={uploadResult.type === 'error' ? 'alert' : 'status'}
              >
                {uploadResult.type === 'success' && (
                  <>
                  <div className="flex items-center justify-between space-x-4">
                    <div className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Image uploaded successfully!</p>
                        <p className="text-sm text-green-700 mt-1 break-all">Analysis ID: {uploadResult.id || 'pending'}</p>
                        {(parsePercentage(uploadResult.healthyPercentage) !== null || parsePercentage(uploadResult.unhealthyPercentage) !== null) && (
                          <p className="text-sm text-green-700 mt-1">
                            Healthy {formatPercentage(uploadResult.healthyPercentage)} · Unhealthy {formatPercentage(uploadResult.unhealthyPercentage)}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setUploadResult(null);
                        setSelectedTab('history');
                      }}
                      className="btn-primary whitespace-nowrap"
                    >
                      View in History
                    </button>
                  </div>
                  {/* New-format result of this upload (crop, health, growth, checks) */}
                  {uploadResult.analysis?.isNewFormat && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
                      <HavvaNewFormatResult analysis={uploadResult.analysis} />
                    </div>
                  )}
                  </>
                )}

                {uploadResult.type === 'no-analysis' && (
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">HAVVA received the photo but returned no analysis.</p>
                      {uploadResult.id && (
                        <p className="text-sm text-amber-800 mt-1 break-all">Reference ID: {uploadResult.id}</p>
                      )}
                      <p className="text-sm text-amber-800 mt-1">
                        Please try again later. If this continues, the analysis service needs attention from the vendor.
                      </p>
                    </div>
                  </div>
                )}

                {uploadResult.type === 'cancelled' && (
                  <p className="text-sm font-medium text-gray-700">{uploadResult.message}</p>
                )}

                {uploadResult.type === 'error' && (
                  <div className="flex items-start">
                    <XCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium text-red-800">{uploadResult.message}</p>
                  </div>
                )}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleUploadSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Camera ID *
                </label>
                <select name="camera" className="input" required>
                  <option value="">Select Camera</option>
                  {filteredCameras.map(camera => (
                    <option key={cameraKey(camera)} value={cameraKey(camera)}>
                      {camera.cameraId} - {camera.note}
                    </option>
                  ))}
                </select>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image File *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                  <div className="space-y-1 text-center">
                    {filePreview ? (
                      <>
                        <img src={filePreview} alt="Preview" className="mx-auto h-32 w-32 object-cover rounded-lg border" />
                        <p className="text-sm font-medium text-gray-900">{selectedFile?.name}</p>
                        <p className="text-xs text-gray-500">{selectedFile ? (selectedFile.size / 1024).toFixed(1) + ' KB' : ''}</p>
                        <button 
                          type="button" 
                          onClick={() => { setSelectedFile(null); setFilePreview(null); }}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </>
                    ) : (
                      <>
                        <CameraIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                            <span>Upload an image</span>
                            <input 
                              id="file-upload" 
                              name="file-upload" 
                              type="file" 
                              className="sr-only" 
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setUploadResult(null);
                                  setSelectedFile(file);
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setFilePreview(reader.result);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">PNG, JPG, JPEG up to 10MB</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-900 mb-2">API Response Format</h4>
                <div className="text-xs text-green-700 space-y-1">
                  <div>• <strong>status:</strong> Success/Failure status</div>
                  <div>• <strong>timeStamp:</strong> Upload timestamp</div>
                  <div>• <strong>id:</strong> Generated upload ID</div>
                  <div>• Processing will begin automatically after upload</div>
                </div>
              </div>

              <div className="flex space-x-4">
                {uploadPhase === 'analyzing' ? (
                  <button
                    type="button"
                    onClick={handleCancelUpload}
                    className="flex-1 btn-secondary"
                  >
                    Cancel Upload
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSelectedTab('list')}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                  disabled={isSubmitting}
                >
                  {uploadPhase === 'analyzing' ? 'Analyzing...' :
                   uploadPhase === 'saving' ? 'Saving...' :
                   isSubmitting ? 'Uploading...' : 'Upload & Analyze'}
                </button>
              </div>
              {uploadPhase === 'analyzing' && (
                <p className="text-xs text-gray-500 text-center">
                  Waiting for HAVVA to analyse the photo. This usually takes under 30 seconds and stops automatically after 2 minutes.
                </p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Modals */}
      <CameraDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModelOpen(false)}
        camera={selectedCamera}
      />

      <EditCameraModal
        isOpen={isEditCameraModalOpen}
        onClose={() => setIsEditCameraModalOpen(false)}
        camera={selectedCamera}
        onSave={handleSaveEditCamera}
        farms={farms}
      />

      {/* Log Modal */}
      {showLogModal && selectedCameraForLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-6 w-6 mr-2 text-purple-600" />
                  API Activity Log
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Camera: <span className="font-semibold">{selectedCameraForLog.cameraId}</span> • 
                  {cameraLogs[selectedCameraForLog.cameraId]?.length || 0} entries
                </p>
              </div>
              <button
                onClick={() => {
                  setShowLogModal(false);
                  setSelectedCameraForLog(null);
                  setLogPage(1); // Reset to first page when closing
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {(!cameraLogs[selectedCameraForLog.cameraId] || cameraLogs[selectedCameraForLog.cameraId].length === 0) ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No API activity recorded for this camera yet.</p>
                  <p className="text-sm text-gray-400 mt-2">Logs will appear here when you perform operations.</p>
                </div>
              ) : (() => {
                const allLogs = [...cameraLogs[selectedCameraForLog.cameraId]].reverse();
                const totalPages = Math.ceil(allLogs.length / logsPerPage);
                const startIndex = (logPage - 1) * logsPerPage;
                const endIndex = startIndex + logsPerPage;
                const currentLogs = allLogs.slice(startIndex, endIndex);
                
                return (
                  <>
                    <div className="space-y-4">
                      {currentLogs.map((log, index) => (
                    <div 
                      key={log.id} 
                      className={`border rounded-lg p-4 ${
                        log.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}
                    >
                      {/* Log Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            log.status === 'success' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {log.operation}
                          </span>
                          {log.status === 'success' ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircleIcon className="h-5 w-5 text-red-600" />
                          )}
                          <span className={`text-sm font-medium ${
                            log.status === 'success' ? 'text-green-900' : 'text-red-900'
                          }`}>
                            {log.status === 'success' ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {new Date(log.timestamp).toLocaleString('en-MY', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </div>
                      </div>

                      {/* HTTP Status */}
                      {log.httpStatus && (
                        <div className="mb-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                            log.httpStatus === 200 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            HTTP {log.httpStatus}
                          </span>
                        </div>
                      )}

                      {/* Request Section */}
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded mr-2">REQUEST</span>
                        </div>
                        <div className="bg-white rounded border border-gray-200 p-3 overflow-x-auto">
                          <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(log.request, null, 2)}
                          </pre>
                        </div>
                      </div>

                      {/* Response Section */}
                      <div>
                        <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center">
                          <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded mr-2">RESPONSE</span>
                        </div>
                        <div className="bg-white rounded border border-gray-200 p-3 overflow-x-auto">
                          <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(log.response, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                      ))}
                    </div>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t">
                        <div className="text-sm text-gray-600">
                          Showing {startIndex + 1}-{Math.min(endIndex, allLogs.length)} of {allLogs.length} logs
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setLogPage(prev => Math.max(1, prev - 1))}
                            disabled={logPage === 1}
                            className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                              <button
                                key={page}
                                onClick={() => setLogPage(page)}
                                className={`px-3 py-1 text-sm rounded ${
                                  page === logPage 
                                    ? 'bg-purple-600 text-white' 
                                    : 'border hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => setLogPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={logPage === totalPages}
                            className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all logs for this camera?')) {
                    setCameraLogs(prev => {
                      const newLogs = { ...prev };
                      delete newLogs[selectedCameraForLog.cameraId];
                      writeStorage(CAMERA_LOGS_KEY, JSON.stringify(newLogs));
                      return newLogs;
                    });
                  }
                }}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
              >
                Clear Logs
              </button>
              <button
                onClick={() => {
                  setShowLogModal(false);
                  setSelectedCameraForLog(null);
                  setLogPage(1); // Reset to first page when closing
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// A render error anywhere on the HAVVA page shows a "Something went wrong"
// card with a Reload button instead of a blank screen. Only this page is wrapped.
const HAVVA = () => (
  <PageErrorBoundary pageName="HAVVA">
    <HAVVAContent />
  </PageErrorBoundary>
);

export default HAVVA;
